import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from '@modules/auth/services/user.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';

import {
    AllPermissions,
    Billplan,
    CouponApplyResponse,
    CouponPreviewResponse,
    DebitoInmediatoAccountInput,
    DebitoInmediatoChangeOptions,
    FiscalInfoRequest,
    PlanCouponSelection,
    PlansBundle,
    PlansPermissions,
    PlansResponse,
    VerifyPaymentRequest,
    VerifyPaymentResponse,
} from '../models/plans.model';
import { ToastService } from './toast.service';

@Injectable({
    providedIn: 'root',
})
export class PlansService {
    public plansBundle$: BehaviorSubject<PlansBundle> = new BehaviorSubject<PlansBundle>(null);
    public planInvalid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public titlePlanInvalid$: BehaviorSubject<string> = new BehaviorSubject<string>('Desbloquea este módulo por sólo $5 🔥');
    public messagePlanInvalid$: BehaviorSubject<string> = new BehaviorSubject<string>('Para poder disfrutar de este modulo debes de mejorar tu plan.');
    public recommendationsMessages$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(['📊Base de datos de clientes con analíticas.', '📩Envío masivo de mensajes de texto a tus clientes.']);

    /**
     * Emite cuando hay que recargar el historial de facturas (pago de plan,
     * cambio de frecuencia o compra de SMS).
     */
    private readonly paymentHistoryRefreshSource$ = new Subject<void>();
    public readonly paymentHistoryRefresh$ = this.paymentHistoryRefreshSource$.asObservable();

    API_URL = environment.API_URL;

    constructor(private http: HttpClient, private toastService: ToastService, private userService: UserService) {}

    /** Notifica a `fina-payment-history-plans` para volver a pedir la lista. */
    refreshPaymentHistory(): void {
        this.paymentHistoryRefreshSource$.next();
    }

    getPaymentMethods$() {
        return this.http.get<any>(`${this.API_URL}/plans/payment-methods`);
    }

    /**
     * Datos fiscales que carga el propio comercio durante la migración a
     * facturación formal. El backend los guarda y dispara la verificación
     * con el SENIAT.
     */
    saveFiscalInfo$(body: FiscalInfoRequest) {
        return this.http.post<any>(`${this.API_URL}/plans/fiscal-info`, body);
    }

    getPaymentHistory$(currentPage: number, pageSize: number) {
        const params = new HttpParams().set('currentPage', currentPage).set('pageSize', pageSize);
        return this.http.get<{ title: string; message: string; data: Billplan[]; totalDocs: number; totalPages: number }>(`${this.API_URL}/plans/bills-plan`, { params });
    }

    payPlan$(data: any) {
        return this.http.post<any>(`${this.API_URL}/plans/pay`, data);
    }

    verifyPayment$(body: VerifyPaymentRequest) {
        return this.http.post<VerifyPaymentResponse>(`${this.API_URL}/plans/verify-payment`, body);
    }

    // ── Canje de cupones en el checkout de renovación (ADM-654) ──
    // `api` proxea a api-admin, que es dueño de la validación y de la matemática
    // del descuento; el `tenantId` sale del JWT, nunca del body. El desglose de la
    // respuesta es la única fuente del total: el front no lo recalcula.

    /** Valida el código contra la factura pendiente sin tocar nada ni consumir uso. */
    previewCoupon$(billPlanId: string, code: string, selection?: PlanCouponSelection) {
        return this.http.post<CouponPreviewResponse>(`${this.API_URL}/plans/coupon/preview`, this.buildCouponBody(billPlanId, code, selection));
    }

    /**
     * Sella el cupón en la factura y reescribe sus montos. Obligatorio antes de
     * cobrar: los tres caminos de pago leen el monto de la factura, no del request.
     */
    applyCoupon$(billPlanId: string, code: string, selection?: PlanCouponSelection) {
        return this.http.post<CouponApplyResponse>(`${this.API_URL}/plans/coupon/apply`, this.buildCouponBody(billPlanId, code, selection));
    }

    /**
     * Los campos de la selección viajan solo si tienen valor: ausentes, api-admin
     * usa el plan y la frecuencia de la factura, que es el contrato original.
     * Mismo patrón que `payWithCard$`.
     */
    private buildCouponBody(billPlanId: string, code: string, selection?: PlanCouponSelection): Record<string, string> {
        const body: Record<string, string> = { billPlanId, code };
        const planType = selection?.planType != null ? String(selection.planType).trim() : '';
        const paymentFrequency = selection?.paymentFrequency != null ? String(selection.paymentFrequency).trim() : '';
        if (planType) {
            body.planType = planType;
        }
        if (paymentFrequency) {
            body.paymentFrequency = paymentFrequency;
        }
        return body;
    }

    // ── Cobro automático con tarjeta (Stripe) ──
    // El tenantId lo deriva la API del JWT (interceptor añade x-access-token),
    // nunca del body. Estos endpoints hacen proxy a API-ADMIN.

    /** Crea (o reutiliza) el cliente y devuelve un SetupIntent para guardar tarjeta. */
    createCardSetupIntent$() {
        return this.http.post<any>(`${this.API_URL}/plans/payment-method/setup-intent`, {});
    }

    /** Confirma la tarjeta a partir del SetupIntent y habilita el cobro automático. */
    saveCard$(setupIntentId: string) {
        return this.http.post<any>(`${this.API_URL}/plans/payment-method/save`, { setupIntentId });
    }

    /** Estado del cobro automático (habilitado, metadatos NO sensibles de la tarjeta). */
    getCard$() {
        return this.http.get<any>(`${this.API_URL}/plans/payment-method`);
    }

    /** Elimina la tarjeta y deshabilita el cobro automático. */
    deleteCard$() {
        return this.http.delete<any>(`${this.API_URL}/plans/payment-method`);
    }

    /**
     * Paga una factura pendiente con la tarjeta guardada estando el cliente
     * presente (on-session). Si el banco pide 3DS, la respuesta trae
     * `data.outcome === 'requires_action'` + `data.clientSecret` para completar la
     * autenticación en el browser con `stripe.handleNextAction`.
     * Opcionalmente aplica cambio de plan y/o frecuencia antes del cobro.
     */
    payWithCard$(
        billPlanId: string | null | undefined,
        options?: {
            planType?: string | null;
            paymentFrequency?: string | null;
            pendingPurchasedUsers?: number | null;
            pendingBlockUserIds?: string[] | null;
            paymentType?: string | null;
            sms?: number | null;
            extraUsersToAdd?: number | null;
            includeSetup?: boolean | null;
            /** `quantity` del paquete de números de control del mix (ADM-732). Lista blanca: sin esta clave se descartaría. */
            controlNumbers?: number | null;
            /** Cupo fiscal extra pedido en la renovación (ADM-740). Lista blanca: sin esta clave se descartaría. */
            extraControlNumbers?: number | null;
        },
    ) {
        const body: Record<string, string | number | string[] | boolean> = {};
        const id = String(billPlanId || '').trim();
        if (id) {
            body.billPlanId = id;
        }
        const planType = options?.planType != null ? String(options.planType).trim() : '';
        const paymentFrequency = options?.paymentFrequency != null ? String(options.paymentFrequency).trim() : '';
        if (planType) {
            body.planType = planType;
        }
        if (paymentFrequency) {
            body.paymentFrequency = paymentFrequency;
        }
        this.appendSeatIntent(body, options);
        this.appendFiscalQuotaIntent(body, options);
        if (options?.paymentType) {
            body.paymentType = String(options.paymentType).trim();
        }
        if (options?.sms != null && Number(options.sms) > 0) {
            body.sms = Math.floor(Number(options.sms));
        }
        if (options?.extraUsersToAdd != null && Number(options.extraUsersToAdd) > 0) {
            body.extraUsersToAdd = Math.floor(Number(options.extraUsersToAdd));
        }
        if (options?.controlNumbers != null && Number(options.controlNumbers) > 0) {
            body.controlNumbers = Math.floor(Number(options.controlNumbers));
        }
        if (options?.includeSetup === true) {
            body.includeSetup = true;
        }
        return this.http.post<any>(`${this.API_URL}/plans/pay-with-card`, body);
    }

    /** Concilia el pago tras completar el 3DS (handleNextAction). */
    confirmCardPayment$(billPlanId: string, intentId: string) {
        return this.http.post<any>(`${this.API_URL}/plans/pay-with-card/confirm`, { billPlanId, intentId });
    }

    // ── Débito Inmediato (BNC) ──
    // El tenantId lo deriva la API del JWT, igual que la tarjeta. Los MONTOS no se
    // mandan nunca desde acá: el servidor cotiza (`getDebitQuote$`) y cobra ese
    // mismo monto, porque el banco ata la clave al monto exacto en bolívares.

    /** Bancos con los que se puede pagar por débito inmediato. */
    getDebitBanks$() {
        return this.http.get<any>(`${this.API_URL}/plans/debito-inmediato/tenant/banks`);
    }

    /** Cuentas de débito guardadas del tenant (enmascaradas). */
    getDebitInstruments$() {
        return this.http.get<any>(`${this.API_URL}/plans/debito-inmediato/tenant/instruments`);
    }

    /** Monto autoritativo del pago (USD, Bs y tasa), calculado por el servidor. */
    getDebitQuote$(billPlanId: string, options?: DebitoInmediatoChangeOptions) {
        return this.http.post<any>(`${this.API_URL}/plans/debito-inmediato/tenant/quote`, this.buildDebitPlanChangeBody(billPlanId, options));
    }

    /** Pide al banco que le mande la clave (OTP) al cliente. */
    requestDebitToken$(billPlanId: string, account: DebitoInmediatoAccountInput, options?: DebitoInmediatoChangeOptions) {
        return this.http.post<any>(`${this.API_URL}/plans/debito-inmediato/tenant/request-token`, {
            ...this.buildDebitPlanChangeBody(billPlanId, options),
            ...account,
        });
    }

    /**
     * Ejecuta el débito con la clave que recibió el cliente. `reference` es el
     * camino de recuperación de un débito que YA se ejecutó: se manda sin clave
     * para registrar ese pago en vez de volver a cobrar.
     */
    chargeDebit$(billPlanId: string, account: DebitoInmediatoAccountInput, payload: { otp?: string; reference?: string }, options?: DebitoInmediatoChangeOptions) {
        const body: Record<string, any> = {
            ...this.buildDebitPlanChangeBody(billPlanId, options),
            ...account,
        };
        const reference = String(payload?.reference || '').trim();
        if (reference) {
            body.reference = reference;
        } else {
            body.otp = String(payload?.otp || '').trim();
        }
        return this.http.post<any>(`${this.API_URL}/plans/debito-inmediato/tenant/charge`, body);
    }

    /**
     * ¿El banco ya liquidó el débito? Solo aplica a los interbancarios, donde el
     * banco acepta la operación y mueve el dinero después. El pago ya está
     * registrado cuando esto se consulta, así que un fallo acá no pierde nada.
     */
    getDebitSettlement$(reference: string) {
        return this.http.post<any>(`${this.API_URL}/plans/debito-inmediato/tenant/settlement`, { reference: String(reference || '').trim() });
    }

    /**
     * Body común: factura, el plan y la frecuencia si el cliente los está
     * cambiando, los cupos pedidos (ADM-743) y el cupo fiscal extra (ADM-740).
     * `quote`, `request-token` y `charge` lo comparten: el banco ata la clave al
     * monto cotizado, así que los tres tienen que mandar exactamente los mismos campos.
     */
    private buildDebitPlanChangeBody(billPlanId: string, options?: DebitoInmediatoChangeOptions): Record<string, string | number | string[]> {
        const body: Record<string, string | number | string[]> = { billPlanId };
        const planType = options?.planType != null ? String(options.planType).trim() : '';
        const paymentFrequency = options?.paymentFrequency != null ? String(options.paymentFrequency).trim() : '';
        if (planType) {
            body.planType = planType;
        }
        if (paymentFrequency) {
            body.paymentFrequency = paymentFrequency;
        }
        this.appendSeatIntent(body, options);
        this.appendFiscalQuotaIntent(body, options);
        return body;
    }

    /**
     * Cupo fiscal extra pedido (ADM-740), misma normalización en tarjeta y Débito
     * Inmediato: entero ≥ 0, incluido el 0 («solo el cupo base»). `null`, `NaN` o
     * negativo no viajan; ausente = mantener la cantidad vigente.
     */
    private appendFiscalQuotaIntent(body: Record<string, string | number | string[] | boolean>, options?: { extraControlNumbers?: number | null }): void {
        if (options?.extraControlNumbers == null) {
            return;
        }
        const quantity = Math.floor(Number(options.extraControlNumbers));
        if (!Number.isFinite(quantity) || quantity < 0) {
            return;
        }
        body.extraControlNumbers = quantity;
    }

    /**
     * Intención de cupos, misma normalización en tarjeta y Débito Inmediato:
     * conteo entero ≥ 0 e ids únicos y recortados, que solo viajan junto al conteo.
     */
    private appendSeatIntent(body: Record<string, string | number | string[] | boolean>, options?: { pendingPurchasedUsers?: number | null; pendingBlockUserIds?: string[] | null }): void {
        if (options?.pendingPurchasedUsers == null || !Number.isFinite(Number(options.pendingPurchasedUsers))) {
            return;
        }
        body.pendingPurchasedUsers = Math.max(0, Math.floor(Number(options.pendingPurchasedUsers)));
        if (!Array.isArray(options.pendingBlockUserIds)) {
            return;
        }
        const uniqueIds: string[] = [];
        const seen = new Set<string>();
        for (const raw of options.pendingBlockUserIds) {
            const id = String(raw).trim();
            if (!id || seen.has(id)) {
                continue;
            }
            seen.add(id);
            uniqueIds.push(id);
        }
        body.pendingBlockUserIds = uniqueIds;
    }

    async updatePlan$(planId: string, paymentFrequency: string) {
        return this.http.put<any>(`${this.API_URL}/plans/${planId}`, { paymentFrequency }).subscribe({
            next: res => {
                this.plansBundle$.next(res.data);
                return res;
            },
            error: err => {
                this.toastService.error(err.error.title, err.error.message);
                return null;
            },
        });
    }

    async obtainPlansBundle(): Promise<any> {
        this.http.get<PlansResponse>(`${this.API_URL}/plans`).subscribe({
            next: res => {
                this.plansBundle$.next(res.data);
                return res.data;
            },
            error: err => {
                console.log('Error obtaining plans.', err);
                this.toastService.error(err.error.title, err.error.message);
                return null;
            },
        });
    }

    // obtainCurrentPlan() {
    //     console.log('Obtaining current plan')
    //     this.http.get<any>(`${this.API_URL}/plans/validate-plan-permissions`).subscribe({
    //         next: (res) => {
    //             this.currentPlan$.next(res.data);
    //         },
    //         error: (err) => {
    //             console.log('Error obtaining the current plan.', err)
    //             this.toastService.error(err.error.title, err.error.message);
    //         }
    //     })
    // }

    /** Comprueba de forma síncrona si el plan o el usuario tienen el permiso (para uso en templates). Coincide con backend: plan O usuario. */
    hasPlanPermissionSync(module: keyof PlansPermissions, permission: AllPermissions): boolean {
        const plansBundle = this.plansBundle$.value;
        const planPerms = plansBundle?.currentPlan?.permissions?.[module];
        const planAllows = Array.isArray(planPerms) && planPerms.includes(permission);

        const user = this.userService.user$?.value;
        const userPerms = (user as any)?.permissions?.[module];
        const userAllows = Array.isArray(userPerms) && userPerms.includes(permission);

        return planAllows || userAllows;
    }

    /** Comprueba solo permisos del plan actual (sin fallback a permisos de usuario). */
    hasCurrentPlanPermissionSync(module: keyof PlansPermissions, permission: AllPermissions): boolean {
        const plansBundle = this.plansBundle$.value;
        const planPerms = plansBundle?.currentPlan?.permissions?.[module];
        return Array.isArray(planPerms) && planPerms.includes(permission);
    }

    async hasPlanPermission(module: keyof PlansPermissions, permission: AllPermissions): Promise<any> {
        return this.plansBundle$.subscribe({
            next: (plansBundle: PlansBundle) => {
                if (plansBundle) {
                    if (plansBundle.currentPlan.permissions[module].includes(permission)) {
                        this.planInvalid$.next(false);
                        return false;
                    } else {
                        this.planInvalid$.next(true);
                        return true;
                    }
                }
            },
        });
    }

    delay(milliseconds: number) {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }
}
